import { Input } from "@/components/ui/input";
import { SalesProductProps } from "../Sales";
import { toast } from "sonner";
import {
  ColoredProductCard,
  ColoredProductIcon,
} from "@/components/ui/colored-product-card";
import { Pagination } from "@/components/Pagination";
import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Minus, Loader2 } from "lucide-react";
import { matchesProductSearch } from "@/utils/productSearch";

export interface SellerContestSummaryItem {
  contest_id: number;
  products_count: number;
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
}

interface ProductSelectionProps {
  products: SalesProductProps[];
  onProductSelect: (productId: number, quantity: number) => void;
  selectedProducts: Array<{ id: number; quantity: number }>;
  pagination?: {
    currentPage: number;
    lastPage: number;
  };
  onPageChange?: (page: number) => void;
  hideSellerBalance?: boolean;
  // Modo lazy: quando informado, a lista de concursos vem do resumo (leve),
  // e os produtos de cada concurso só são buscados ao expandir.
  contestsSummary?: SellerContestSummaryItem[];
  loadingContests?: Set<number>;
  onExpandContest?: (contestId: number) => void;
}

interface AggregatedProduct extends SalesProductProps {
  aggregated_ids: number[];
  _requested_quantity_sum?: number;
  _marketplace_balance_sum?: number;
  _desk_balance_sum?: number;
  desk_balance?: number;
}

const lightenColor = (color: string, percent: number) => {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const aggregateProducts = (
  products: SalesProductProps[]
): AggregatedProduct[] => {
  const aggregated = Array.isArray(products)
    ? products
        ?.filter((item) => item?.contest?.id)
        ?.reduce((acc, product) => {
          const key = `${product.contest_id}-${product.cp_classification_id}`;
          const canUseUnityBalance =
            product.unity?.seller_received_without_distribution === true;
          const unityBalance =
            canUseUnityBalance &&
            Array.isArray(
              product?.contest_product_classification?.cp_unity_balance
            )
              ? product.contest_product_classification.cp_unity_balance.reduce(
                  (sum, item) => sum + (item.unity_balance || 0),
                  0
                )
              : 0;
          if (!acc[key]) {
            acc[key] = {
              ...product,
              contest_product_classification: {
                ...product.contest_product_classification,
                cp_unity_balance:
                  product.contest_product_classification?.cp_unity_balance,
              },
              quota_quantity: product.quota_quantity,
              aggregated_ids: [product.id],
              _unity_balance_sum: unityBalance,
              _requested_quantity_sum: product.requested_quantity || 0,
              _marketplace_balance_sum: product.marketplace_balance || 0,
              _desk_balance_sum: product.desk_balance || 0,
            };
          } else {
            acc[key].quota_quantity += product.quota_quantity;
            acc[key].aggregated_ids.push(product.id);
            acc[key]._unity_balance_sum =
              (acc[key]._unity_balance_sum || 0) + unityBalance;
            acc[key]._requested_quantity_sum =
              (acc[key]._requested_quantity_sum || 0) +
              (product.requested_quantity || 0);
            acc[key]._marketplace_balance_sum =
              (acc[key]._marketplace_balance_sum || 0) +
              (product.marketplace_balance || 0);
            acc[key]._desk_balance_sum =
              (acc[key]._desk_balance_sum || 0) + (product.desk_balance || 0);
          }

          return acc;
        }, {} as Record<string, AggregatedProduct & { _unity_balance_sum?: number; _requested_quantity_sum?: number; _desk_balance_sum?: number }>)
    : {};

  return Object.values(aggregated);
};

export const ProductSelection = ({
  products,
  onProductSelect,
  selectedProducts,
  pagination,
  onPageChange,
  hideSellerBalance = false,
  contestsSummary,
  loadingContests,
  onExpandContest,
}: ProductSelectionProps) => {
  const [expandedContests, setExpandedContests] = useState<Set<string>>(new Set());
  const [productSearchTerms, setProductSearchTerms] = useState<Record<string, string>>({});

  const toggleContest = (contestId: number) => {
    const key = String(contestId);
    const isCurrentlyExpanded = expandedContests.has(key);

    setExpandedContests((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });

    if (!isCurrentlyExpanded && contestsSummary && onExpandContest) {
      const alreadyLoaded = products.some((p) => p.contest_id === contestId);
      if (!alreadyLoaded) {
        onExpandContest(contestId);
      }
    }
  };

  const handleQuantityChange = (product: AggregatedProduct, value: string) => {
    if (value === "") {
      onProductSelect(product.id, 0);
      return;
    }

    const quantity = Number(value);

    if (quantity < 0) {
      toast.error("Quantidade não pode ser negativa");
      return;
    }

    const sellerBalance = product.cp_seller_balance?.seller_balance || 0;
    const canUseUnityBalance =
      product.unity?.seller_received_without_distribution == true;
    const unityBalance =
      canUseUnityBalance &&
      Array.isArray(product.contest_product_classification?.cp_unity_balance)
        ? product.contest_product_classification.cp_unity_balance.reduce(
            (sum, item) => sum + (item.unity_balance || 0),
            0
          )
        : 0;
    const totalAvailable = sellerBalance + unityBalance;

    if (quantity > totalAvailable) {
      const message = canUseUnityBalance
        ? `Quantidade máxima disponível: ${totalAvailable} (Vendedor: ${sellerBalance} + Unidade: ${unityBalance})`
        : `Quantidade máxima disponível: ${sellerBalance}`;
      toast.error(message);
      return;
    }

    onProductSelect(product.id, quantity);
  };

  const renderProductCards = (
    contestProducts: AggregatedProduct[],
    lightLotteryColor: string
  ) =>
    contestProducts.map((product) => {
        const selectedProduct = selectedProducts.find(
          (p) => p.id === product.id
        );
        const sellerBalance = product.cp_seller_balance?.seller_balance || 0;
        const canUseUnityBalance =
          product.unity?.seller_received_without_distribution === true;
        const unityBalance =
          canUseUnityBalance &&
          Array.isArray(
            product.contest_product_classification?.cp_unity_balance
          )
            ? product.contest_product_classification.cp_unity_balance.reduce(
                (sum, item) => sum + (item.unity_balance || 0),
                0
              )
            : 0;
        const totalAvailable = sellerBalance + unityBalance;
        const requestedQuantity = product._requested_quantity_sum || 0;
        const marketplaceBalance = product._marketplace_balance_sum || 0;
        const deskBalance = product._desk_balance_sum || 0;
        const isOverLimit = (selectedProduct?.quantity || 0) > totalAvailable;
        const bgColor = product?.contest_product_classification?.lottery?.color;
        return (
          <ColoredProductCard
            key={product.id}
            bgColor={isOverLimit ? "#ef4444" : lightLotteryColor}
          >
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2">
                <ColoredProductIcon bgColor={bgColor} />

                <p className="text-sm font-medium" style={{ color: "white" }}>
                  {product?.contest_product_classification?.lottery?.name}
                </p>
              </div>
              <span className="text-sm flex flex-row gap-2">
                <h4 className="font-semibold text-white">
                  {product.contest_product_classification.product_name}
                </h4>
                <span className="text-white">-</span>
                <span className="text-white">
                  {
                    product?.contest_product_classification
                      ?.product_classification?.name
                  }
                </span>
              </span>
              {(marketplaceBalance > 0 || deskBalance > 0) && (
                <div className="flex flex-col items-center gap-2 py-1">
                  <div className="flex items-center gap-2">
                    {marketplaceBalance > 0 && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-xs font-bold shadow-sm">
                        MKT: {marketplaceBalance}
                      </span>
                    )}
                    {deskBalance > 0 && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sky-400 text-sky-950 text-xs font-bold shadow-sm">
                        Escritório: {deskBalance}
                      </span>
                    )}
                  </div>
                  <div className="w-full border-t border-white/20" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm font-medium ${
                    totalAvailable === 0 ? "text-red-200" : "text-white"
                  }`}
                >
                  Disponível: {totalAvailable}
                </span>
                {requestedQuantity > 0 && (
                  <span className="text-sm font-medium text-white/80">
                    Solicitado: {requestedQuantity}
                  </span>
                )}
              </div>
              {!hideSellerBalance && (
                <div className="text-xs text-white/80">
                  Vendedor: {sellerBalance}
                  {canUseUnityBalance && ` | Unidade: ${unityBalance}`}
                </div>
              )}
              <div className="mt-2">
                <Input
                  type="number"
                  min="0"
                  max={totalAvailable}
                  placeholder="Quantidade"
                  value={selectedProduct?.quantity || ""}
                  onChange={(e) => handleQuantityChange(product, e.target.value)}
                  className={`w-full bg-white ${
                    isOverLimit ? "border-red-500" : ""
                  }`}
                  disabled={totalAvailable === 0}
                />
                {isOverLimit && (
                  <p className="text-red-200 text-xs mt-1 font-medium">
                    Quantidade excede o limite disponível
                  </p>
                )}
              </div>
            </div>
          </ColoredProductCard>
        );
      });

  // Aceita número, intervalo e combinações: "19", "19-25", "B19 - B25", "19-25, 30"
  const handleSearchTermChange = (contestKey: string, rawValue: string) => {
    const value = rawValue.replace(/[^0-9A-Za-z,;\-\s]/g, "");

    setProductSearchTerms((prev) => ({ ...prev, [contestKey]: value }));
  };

  const renderSearchInput = (
    contestKey: string,
    searchTerm: string,
    id: string,
    className: string
  ) => (
    <Input
      id={id}
      type="text"
      inputMode="text"
      placeholder="Nº ou intervalo (ex: 19-25)"
      value={searchTerm}
      onChange={(e) => handleSearchTermChange(contestKey, e.target.value)}
      className={className}
    />
  );

  const renderProductPanel = (
    filteredProducts: AggregatedProduct[],
    lotteryColor: string,
    lightLotteryColor: string,
    contestKey: string,
    searchTerm: string
  ) => (
    <div
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: lotteryColor }}
    >
      <div className="p-4 pb-4 flex lg:hidden items-center gap-2">
        <label
          htmlFor={`product-search-panel-${contestKey}`}
          className="text-sm font-medium text-white"
        >
          B:
        </label>
        {renderSearchInput(
          contestKey,
          searchTerm,
          `product-search-panel-${contestKey}`,
          "max-w-[200px] bg-white"
        )}
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center text-white/80 pb-8 lg:pt-8">
          Nenhum produto encontrado para essa busca
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-4 pb-4 lg:pt-4 max-h-[500px] overflow-y-auto">
          {renderProductCards(filteredProducts, lightLotteryColor)}
        </div>
      )}
    </div>
  );

  // Modo lazy por concurso: o resumo (leve) já traz loteria/concurso/contagem.
  // Os produtos de cada concurso só existem em `products` depois de expandido.
  if (contestsSummary) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <h2 className="text-2xl font-semibold text-center">
          Selecione os Produtos
        </h2>

        {contestsSummary.map((summary) => {
          const contestId = summary.contest_id;
          const contestKey = String(contestId);
          const lotteryColor = summary.lottery?.color || "#6b7280";
          const lotteryName = summary.lottery?.name;
          const lightLotteryColor = lotteryColor
            ? lightenColor(lotteryColor, 80)
            : "#f3f4f6";

          const isExpanded = expandedContests.has(contestKey);
          const isLoading = loadingContests?.has(contestId) || false;
          const contestProducts = aggregateProducts(
            products.filter((p) => p.contest_id === contestId)
          );
          const selectedCount = contestProducts.filter((p) =>
            selectedProducts.some((sp) => sp.id === p.id && sp.quantity > 0)
          ).length;

          const searchTerm = productSearchTerms[contestKey] || "";
          const filteredContestProducts = searchTerm
            ? contestProducts.filter((p) =>
                matchesProductSearch(
                  p.contest_product_classification?.product_name || "",
                  searchTerm
                )
              )
            : contestProducts;

          return (
            <div key={contestId} className="mb-1">
              <div
                className={`p-4 cursor-pointer flex items-center justify-between transition-all duration-200 hover:scale-[1.01] hover:brightness-110 ${
                  isExpanded ? "rounded-t-lg" : "rounded-lg"
                }`}
                style={{
                  backgroundColor: lotteryColor,
                  boxShadow: isExpanded
                    ? "none"
                    : "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -2px rgba(0, 0, 0, 0.1), 0 8px 10px -3px rgba(0, 0, 0, 0.15)",
                }}
                onClick={() => toggleContest(contestId)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isExpanded ? (
                    <ChevronDown className="h-6 w-6 text-white shrink-0" />
                  ) : (
                    <ChevronRight className="h-6 w-6 text-white shrink-0" />
                  )}
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-white truncate">
                      {lotteryName}
                    </h2>
                    <p className="text-sm text-white/80">
                      concurso {summary.contest?.name} -{" "}
                      {summary.products_count} produtos
                      {selectedCount > 0 && (
                        <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">
                          {selectedCount} selecionado(s)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {isExpanded && contestProducts.length > 0 && (
                    <div
                      className="hidden lg:flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label
                        htmlFor={`product-search-header-${contestKey}`}
                        className="text-sm font-medium text-white"
                      >
                        B:
                      </label>
                      {renderSearchInput(
                        contestKey,
                        searchTerm,
                        `product-search-header-${contestKey}`,
                        "max-w-[200px] bg-white"
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                    {isExpanded ? (
                      <Minus className="h-5 w-5 text-white" />
                    ) : (
                      <Plus className="h-5 w-5 text-white" />
                    )}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="bg-white rounded-b-lg shadow-lg p-4">
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Carregando produtos...
                    </div>
                  ) : contestProducts.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      Nenhum produto disponível para este concurso
                    </div>
                  ) : (
                    renderProductPanel(
                      filteredContestProducts,
                      lotteryColor,
                      lightLotteryColor,
                      contestKey,
                      searchTerm
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}

        {contestsSummary.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            Nenhum produto disponível para este vendedor
          </div>
        )}
      </div>
    );
  }

  // Modo legado: lista de produtos já vem completa em `products`
  // (usado por telas que não filtram por vendedor+concurso, ex.: pré-venda).
  const aggregatedArray = aggregateProducts(products);
  const groupedProducts = aggregatedArray.reduce((acc, product) => {
    const contestId = product.contest_id;
    if (!acc[contestId]) {
      acc[contestId] = [];
    }
    acc[contestId].push(product);
    return acc;
  }, {} as Record<number, AggregatedProduct[]>);

  return (
    <div className="flex flex-col gap-6 p-6">
      <h2 className="text-2xl font-semibold text-center">
        Selecione os Produtos
      </h2>

      {Object.entries(groupedProducts).map(([contestIdStr, contestProducts]) => {
        const contestId = Number(contestIdStr);
        const lotteryColor =
          contestProducts?.[0]?.contest_product_classification?.lottery?.color;
        const lotteryName =
          contestProducts?.[0]?.contest_product_classification?.lottery?.name;

        const lightLotteryColor = lotteryColor
          ? lightenColor(lotteryColor, 80)
          : "#f3f4f6";

        const isExpanded = expandedContests.has(contestIdStr);
        const totalProducts = contestProducts.length;
        const selectedCount = contestProducts.filter((p) =>
          selectedProducts.some((sp) => sp.id === p.id && sp.quantity > 0)
        ).length;

        const searchTerm = productSearchTerms[contestIdStr] || "";
        const filteredContestProducts = searchTerm
          ? contestProducts.filter((p) =>
              matchesProductSearch(
                p.contest_product_classification?.product_name || "",
                searchTerm
              )
            )
          : contestProducts;

        return (
          <div key={contestIdStr} className="mb-1">
            <div
              className={`p-4 cursor-pointer flex items-center justify-between transition-all duration-200 hover:scale-[1.01] hover:brightness-110 ${
                isExpanded ? "rounded-t-lg" : "rounded-lg"
              }`}
              style={{
                backgroundColor: lotteryColor,
                boxShadow: isExpanded
                  ? "none"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -2px rgba(0, 0, 0, 0.1), 0 8px 10px -3px rgba(0, 0, 0, 0.15)",
              }}
              onClick={() => toggleContest(contestId)}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-6 w-6 text-white" />
                ) : (
                  <ChevronRight className="h-6 w-6 text-white" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {lotteryName}
                  </h2>
                  <p className="text-sm text-white/80">
                    concurso {contestProducts?.[0]?.contest?.name} -{" "}
                    {totalProducts} produtos
                    {selectedCount > 0 && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">
                        {selectedCount} selecionado(s)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {isExpanded && totalProducts > 0 && (
                  <div
                    className="hidden lg:flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label
                      htmlFor={`product-search-header-${contestIdStr}`}
                      className="text-sm font-medium text-white"
                    >
                      B:
                    </label>
                    {renderSearchInput(
                      contestIdStr,
                      searchTerm,
                      `product-search-header-${contestIdStr}`,
                      "max-w-[200px] bg-white"
                    )}
                  </div>
                )}
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                  {isExpanded ? (
                    <Minus className="h-5 w-5 text-white" />
                  ) : (
                    <Plus className="h-5 w-5 text-white" />
                  )}
                </div>
              </div>
            </div>

            {isExpanded && (
              <div className="bg-white rounded-b-lg shadow-lg p-4">
                {renderProductPanel(
                  filteredContestProducts,
                  lotteryColor,
                  lightLotteryColor,
                  contestIdStr,
                  searchTerm
                )}
              </div>
            )}
          </div>
        );
      })}

      {Object.keys(groupedProducts).length === 0 && (
        <div className="text-center text-gray-500 py-8">
          Nenhum produto disponível para esta loteria
        </div>
      )}

      {pagination && pagination.lastPage > 1 && onPageChange && (
        <Pagination
          current_page={pagination.currentPage}
          last_page={pagination.lastPage}
          handleNext={() => onPageChange(pagination.currentPage + 1)}
          handlePrevious={() => onPageChange(pagination.currentPage - 1)}
          handleLastPage={() => onPageChange(pagination.lastPage)}
        />
      )}
    </div>
  );
};
